
import { createSignal, onMount, onCleanup } from 'solid-js';
import type { PagiflowInstance, PagiflowOptions } from './types';


export interface PagiflowController {
  /** Attach to your track element: `<div ref={slider.ref}>` */
  ref: (el: HTMLElement) => void;
  /** Reactive current slide index (0-based). Call as `slider.currentIndex()`. */
  currentIndex: () => number;
  /** Raw PagiflowInstance — null until mounted. Call as `slider.instance()`. */
  instance: () => PagiflowInstance | null;
  /** Go to next slide */
  next(): void;
  /** Go to previous slide */
  prev(): void;
  /** Go to a specific slide */
  goTo(index: number, opts?: { silent?: boolean; instant?: boolean }): void;
  /** Start autoplay */
  play(): void;
  /** Pause autoplay */
  pause(): void;
  /** Resume autoplay */
  resume(): void;
  /** Toggle play / pause */
  togglePlayPause(): void;
  /** Update options (optionally rebuild) */
  setOptions(opts: Partial<PagiflowOptions>, rebuild?: boolean): void;
  /** Destroy and reinitialise */
  reInit(opts?: Partial<PagiflowOptions>): void;
}

// ─── Loader ───────────────────────────────────────────────────────────────────

function getPagiflow(): ((selector: string | HTMLElement, opts?: PagiflowOptions) => PagiflowInstance) | null {
  if (typeof window !== 'undefined' && (window as any).Pagiflow) {
    return (window as any).Pagiflow;
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('pagiflow');
    return mod.default ?? mod;
  } catch {
    return null;
  }
}

// ─── createPagiflow ───────────────────────────────────────────────────────────

/**
 * SolidJS primitive that initialises and controls a Pagiflow slider.
 * Attach `slider.ref` to any DOM element; all other API methods are stable
 * function references safe to pass as event handlers.
 *
 * @example
 * ```tsx
 * function MySlider() {
 *   const slider = createPagiflow({ loop: true, nav: true });
 *   return (
 *     <>
 *       <div ref={slider.ref}>
 *         <div>Slide 1</div>
 *         <div>Slide 2</div>
 *       </div>
 *       <button onClick={slider.prev}>‹</button>
 *       <span>{slider.currentIndex() + 1}</span>
 *       <button onClick={slider.next}>›</button>
 *     </>
 *   );
 * }
 * ```
 */
export function createPagiflow(options: PagiflowOptions = {}): PagiflowController {
  const [currentIndex, setCurrentIndex] = createSignal(options.startIndex ?? 0);
  const [instance, setInstance] = createSignal<PagiflowInstance | null>(null);

  // The ref callback — SolidJS calls this synchronously when the element mounts
  const ref = (el: HTMLElement) => {
    onMount(() => {
      const existing = (el as any).pagiflowInstance as PagiflowInstance | undefined;
      if (existing && !existing.destroyed) return;

      const Pagiflow = getPagiflow();
      if (!Pagiflow) {
        console.error('[createPagiflow] Pagiflow core not found. Install pagiflow or load it via CDN.');
        return;
      }

      const attrValue = `pf-${Math.random().toString(36).slice(2)}`;
      const selector = `[data-pagiflow-solid-id="${attrValue}"]`;
      el.setAttribute('data-pagiflow-solid-id', attrValue);

      const inst = Pagiflow(selector, options) as PagiflowInstance | null;
      if (!inst) {
        el.removeAttribute('data-pagiflow-solid-id');
        return;
      }

      const syncLayout = () => { (inst as any).update?.(); };

      const raf1 = requestAnimationFrame(() => syncLayout());
      const raf2 = requestAnimationFrame(() => syncLayout());
      const timeoutId = setTimeout(() => syncLayout(), 80);
      window.addEventListener('load', syncLayout, { once: true });

      let resizeObserver: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => syncLayout());
        resizeObserver.observe(el);
      }

      inst.onSlideChange((idx) => setCurrentIndex(idx));

      setInstance(inst);

      onCleanup(() => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
        clearTimeout(timeoutId);
        window.removeEventListener('load', syncLayout);
        resizeObserver?.disconnect();
        const current = instance();
        if (current && !current.destroyed) {
          try { current.destroy(); } catch { /* ignore */ }
        }
        delete (el as any).pagiflowInstance;
        setInstance(null);
      });
    });
  };

  // ─── Stable API ────────────────────────────────────────────────────────────
  return {
    ref,
    currentIndex,
    instance,
    next:            () => instance()?.next(),
    prev:            () => instance()?.prev(),
    goTo:            (i, o) => instance()?.goTo(i, o),
    play:            () => instance()?.play(),
    pause:           () => instance()?.pause(),
    resume:          () => instance()?.resume(),
    togglePlayPause: () => instance()?.togglePlayPause(),
    setOptions:      (o, r) => instance()?.setOptions(o, r),
    reInit:          (o) => instance()?.reInit(o),
  };
}
